import React, { useState, useEffect } from 'react';
import supabase from '../config/supabase';
import { queryCountSessionsArchived } from '../utils/directFetch';

// Countertop palette — matches the app theme in sidebar-layout.css. No decorative
// background image/overlay: the page is a clean, standard surface like the rest of
// the app.
const C = {
  pageBg: '#FEFCFA',
  cardBg: '#FFFFFF',
  cardBorder: '#E8DDD4',
  titleText: '#5A4A42',
  bodyText: '#5A4A42',
  mutedText: '#8B7B73',
  badge: '#8B7B73',
  badgeBg: '#F0E9E1',
  recoverBg: '#4A7A4A',
  recoverText: '#fff',
  deleteBg: '#C0392B',
  deleteText: '#fff',
  voidBg: '#A9762B',
  voidText: '#fff',
  headerBorder: '#E8DDD4',
};

const SessionArchivePage = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recoveringId, setRecoveringId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [voidingId, setVoidingId] = useState(null);

  useEffect(() => {
    loadArchivedSessions();
  }, []);

  const loadArchivedSessions = async () => {
    setLoading(true);
    try {
      const storedStoreId = localStorage.getItem('selectedStoreId');
      if (!storedStoreId) return;
      const storeId = parseInt(storedStoreId);

      const { data, error } = await queryCountSessionsArchived(storeId);
      if (error) {
        console.error('Error loading archived sessions:', error);
        return;
      }

      // Group by count_group_id so stacks stay together
      const grouped = {};
      (data || []).forEach(s => {
        const gid = s.count_group_id || s.id;
        if (!grouped[gid]) grouped[gid] = [];
        grouped[gid].push(s);
      });

      // Flatten but attach stack info
      const enriched = (data || []).map(s => {
        const gid = s.count_group_id || s.id;
        return { ...s, stackSize: grouped[gid].length, stackSessions: grouped[gid] };
      });

      setSessions(enriched);
    } catch (err) {
      console.error('Error loading archived sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecover = async (session) => {
    setRecoveringId(session.id);
    try {
      // Check if this is part of a stack
      const stackSessions = session.stackSessions || [session];
      const isStack = stackSessions.length > 1;

      let sessionIds = [session.id];

      if (isStack) {
        const recoverAll = window.confirm(
          `"${session.name}" is part of a stack of ${stackSessions.length} archived counts.\n\nRecover the entire stack?\n\nClick OK for entire stack, Cancel for just this count.`
        );
        if (recoverAll) {
          sessionIds = stackSessions.map(s => s.id);
        }
      }

      for (const id of sessionIds) {
        const { error } = await supabase
          .from('count_sessions')
          .update({ is_archived: false, archived_at: null })
          .eq('id', id);
        if (error) throw error;
      }

      await loadArchivedSessions();
    } catch (err) {
      console.error('Error recovering session:', err);
      alert('Error recovering session: ' + err.message);
    } finally {
      setRecoveringId(null);
    }
  };

  /**
   * Void a completed session: sets voided_at so it is excluded from active
   * reports and inventory totals, but the record remains visible here in the
   * audit/history view.
   */
  const handleVoid = async (session) => {
    const confirmed = window.confirm(
      `Void "${session.name || 'Unnamed count'}"?\n\nVoiding marks this count as invalid and excludes it from reports and inventory totals, but keeps the record visible here for auditing.\n\nThis cannot be automatically undone — contact support to un-void a session.`
    );
    if (!confirmed) return;
    setVoidingId(session.id);
    try {
      const { error } = await supabase
        .from('count_sessions')
        .update({ voided_at: new Date().toISOString() })
        .eq('id', session.id);
      if (error) throw error;
      await loadArchivedSessions();
    } catch (err) {
      console.error('Error voiding session:', err);
      alert('Error voiding session: ' + err.message);
    } finally {
      setVoidingId(null);
    }
  };

  /**
   * Permanent delete — only allowed for non-completed sessions.
   * Completed sessions must be archived or voided instead.
   */
  const handleDelete = async (session) => {
    if (session.status === 'completed') {
      alert(
        `"${session.name || 'This count'}" is a completed session and cannot be permanently deleted.\n\nCompleted sessions preserve your audit trail. Use Void to exclude it from reports while keeping the record.`
      );
      return;
    }

    const confirmed = window.confirm(
      `This will destroy the count records in this count, proceed to delete?\n\n"${session.name || 'Unnamed count'}" — ${formatDate(session.session_date)}`
    );
    if (!confirmed) return;
    setDeletingId(session.id);
    try {
      const { error } = await supabase.from('count_sessions').delete().eq('id', session.id);
      if (error) throw error;
      await loadArchivedSessions();
    } catch (err) {
      console.error('Error deleting session:', err);
      alert('Error deleting session: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <div className="session-page-scroll" style={{ minHeight: '100vh', backgroundColor: C.pageBg, color: C.bodyText, padding: '24px', fontFamily: "'Open Sans', sans-serif" }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '24px', paddingBottom: '16px', borderBottom: `1px solid ${C.headerBorder}` }}>
          <div>
            <h2 style={{ color: C.titleText, margin: '0 0 4px', fontWeight: '800', fontSize: '1.4rem', letterSpacing: '-0.02em' }}>
              🗂 Session Archive
            </h2>
            <p style={{ color: C.mutedText, margin: 0, fontSize: '0.82rem' }}>
              Archived counts are hidden from the main view but remain in reports. Recover, void, or permanently delete (draft/in-progress only) from here.
            </p>
          </div>
          <a href="/count.html?view=overview" style={{ padding: '8px 16px', backgroundColor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '8px', color: C.bodyText, textDecoration: 'none', fontSize: '0.85rem', fontWeight: '700' }}>
            ← Back to Counts
          </a>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: C.mutedText }}>
            <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>
            <p style={{ marginTop: '12px' }}>Loading archived sessions…</p>
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 24px', backgroundColor: C.cardBg, borderRadius: '12px', border: `1px dashed ${C.cardBorder}` }}>
            <div style={{ fontSize: '3rem', marginBottom: '12px', opacity: 0.35 }}>🗂</div>
            <p style={{ color: C.bodyText, fontSize: '1rem', margin: '0 0 4px' }}>No archived sessions.</p>
            <p style={{ color: C.mutedText, fontSize: '0.85rem', margin: 0 }}>Sessions you archive will appear here.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
            {sessions.map(session => {
              const isRecovering = recoveringId === session.id;
              const isDeleting = deletingId === session.id;
              const isVoiding = voidingId === session.id;
              const isCompleted = session.status === 'completed';
              const isVoided = !!session.voided_at;

              // Prefer snapshot name for display; fall back to session name
              const templateDisplay = session.template_name_snapshot || null;

              return (
                <div key={session.id} style={{ backgroundColor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '10px', padding: '14px', transition: 'box-shadow 0.2s', boxShadow: '0 1px 4px rgba(60,48,32,0.06)', opacity: isVoided ? 0.72 : 1 }}>
                  {/* Header row */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ color: C.titleText, fontWeight: '800', fontSize: '0.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {session.name}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      {isVoided && (
                        <span style={{ backgroundColor: '#f3e8d8', color: '#8b5a2b', fontSize: '0.6rem', padding: '2px 8px', borderRadius: '4px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                          Voided
                        </span>
                      )}
                      <span style={{ backgroundColor: C.badgeBg, color: C.badge, fontSize: '0.6rem', padding: '2px 8px', borderRadius: '4px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                        Archived
                      </span>
                    </div>
                  </div>

                  {/* Details */}
                  <div style={{ fontSize: '0.75rem', color: C.mutedText, lineHeight: '1.7' }}>
                    <div>📅 {formatDate(session.session_date)}</div>
                    <div>📊 {(() => { const c = session.items_counted || 0; const t = session.total_items || 0; return t > 0 ? `${c} / ${t} items counted` : c > 0 ? `${c} items counted` : '0 items counted'; })()}</div>
                    <div>🗂 Archived: {formatDate(session.archived_at)}</div>
                    {templateDisplay && <div>📋 Template: {templateDisplay}{session.template_type_snapshot ? ` (${session.template_type_snapshot})` : ''}</div>}
                    {session.stackSize > 1 && <div>🔗 Part of stack ({session.stackSize} counts)</div>}
                    <div>Status: {session.status}{isVoided ? ' · Voided ' + formatDate(session.voided_at) : ''}</div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                    {isVoided ? (
                      <span
                        title="Voided sessions cannot be recovered — they remain here for audit purposes only"
                        style={{ flex: 1, minWidth: '80px', padding: '6px 10px', fontSize: '0.72rem', color: C.mutedText, fontStyle: 'italic', alignSelf: 'center' }}
                      >
                        🔒 Audit record only
                      </span>
                    ) : (
                      <button
                        onClick={() => handleRecover(session)}
                        disabled={isRecovering || isDeleting || isVoiding}
                        style={{ flex: 1, minWidth: '80px', padding: '6px 10px', backgroundColor: C.recoverBg, border: 'none', borderRadius: '6px', color: C.recoverText, fontSize: '0.78rem', fontWeight: '700', cursor: isRecovering ? 'wait' : 'pointer', opacity: isRecovering || isDeleting || isVoiding ? 0.55 : 1 }}
                      >
                        {isRecovering ? 'Recovering…' : '↩ Recover'}
                      </button>
                    )}

                    {isCompleted && !isVoided && (
                      <button
                        onClick={() => handleVoid(session)}
                        disabled={isDeleting || isRecovering || isVoiding}
                        title="Void this count — excludes it from reports while keeping the audit record"
                        style={{ padding: '6px 10px', backgroundColor: C.voidBg, border: 'none', borderRadius: '6px', color: C.voidText, fontSize: '0.78rem', fontWeight: '700', cursor: isVoiding ? 'wait' : 'pointer', opacity: isDeleting || isRecovering || isVoiding ? 0.55 : 1 }}
                      >
                        {isVoiding ? 'Voiding…' : '⊘ Void'}
                      </button>
                    )}

                    {!isCompleted && (
                      <button
                        onClick={() => handleDelete(session)}
                        disabled={isDeleting || isRecovering || isVoiding}
                        style={{ padding: '6px 10px', backgroundColor: 'transparent', border: `1px solid ${C.cardBorder}`, borderRadius: '6px', color: C.deleteBg, fontSize: '0.78rem', fontWeight: '700', cursor: isDeleting ? 'wait' : 'pointer', opacity: isDeleting || isRecovering || isVoiding ? 0.55 : 1 }}
                      >
                        {isDeleting ? 'Deleting…' : '🗑 Delete'}
                      </button>
                    )}

                    {isCompleted && (
                      <span
                        title="Completed counts cannot be permanently deleted — use Void to exclude from reports"
                        style={{ padding: '6px 10px', fontSize: '0.72rem', color: C.mutedText, fontStyle: 'italic', alignSelf: 'center' }}
                      >
                        🔒 No delete
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionArchivePage;
